import { cva } from 'class-variance-authority';
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useTranslation } from 'next-i18next';

import { otherStorePageSlugs } from '@boss/constants/b2b-b2c';
import { useRouter } from '@boss/hooks';
import { store as storeService } from '@boss/services';
import { Environment } from '@boss/types/b2b-b2c';
import { StoreCard } from '@boss/ui';

import { SEO, StoreFinder } from '../../components';
import { PageTypes } from '../../constants';
import { useOrderStoresByProx } from '../../hooks';
import Layout from '../../layout/default';
import { handleStores } from '../../utils';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { mapNextToContentfulLocaleMarket } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';
import { storeCardCtaButtonType, storeCardCtaVariant, storeConfig, storeFinderVariant } from '../../utils/options';

const backgroundColor = cva('relative break-before break-after', {
  variants: {
    variant: {
      primary: 'bg-beige before:bg-beige after:bg-beige',
      secondary: 'bg-gray-lightest before:bg-gray-lightest  after:bg-gray-lightest',
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
});

export const getStaticProps = async ({ preview, locale }: GetStaticPropsContext) => {
  if (!locale || locale === 'default') {
    return {
      notFound: true,
    };
  }

  const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);

  const [stores, navigation] = await Promise.all([
    storeService.getAllStores({ locale }),
    getMappedNavigation(contentfulLocaleMarket, !!preview),
  ]);

  return {
    props: {
      pageType: PageTypes.STORE_OVERVIEW,
      stores: handleStores(stores, { ...storeConfig, fetchOnlyType: 'Geen', filterTypesOut: [] }),
      preview: !!preview,
      navigation,
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      ...(await getServerSideTranslations(locale, ['common', 'stores'])),
    },
  };
};

export const Index = ({ navigation, preview, environment, stores }: InferGetStaticPropsType<typeof getStaticProps>) => {
  const localizedSlugs = otherStorePageSlugs;
  const { t } = useTranslation('stores');
  const { locale } = useRouter();
  const pageTitle = t('storesLabel', { ns: 'common' }) ?? '';
  const pageDescription = pageTitle; // TODO: Update with final value once this is known
  const {
    location,
    sortStoresByLocationId,
    filteredStores,
    isCurrentLocationSearch,
    resetStoreOrder,
    sortStoresByCoord,
  } = useOrderStoresByProx(stores);

  const crumbles = [{ href: localizedSlugs[locale], label: pageTitle }];

  return (
    <SEO
      breadcrumbs={crumbles}
      description={pageDescription}
      localizedSlugs={localizedSlugs}
      navigation={navigation}
      title={pageTitle}
    >
      <Layout
        breadcrumbClassName="mb-4 md:mb-6"
        breadcrumbWrapperClassName={backgroundColor({ variant: storeFinderVariant })}
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={localizedSlugs}
        navigation={navigation}
        preview={preview}
      >
        <StoreFinder
          extended
          noFilters
          onSearch={sortStoresByLocationId}
          sortByCurrentLocation={sortStoresByCoord}
          translations={{
            ...t('storeFinder', { returnObjects: true }),
            ...t('affiliateFinder', { returnObjects: true }),
          }}
          variant={storeFinderVariant}
        />
        {location && (
          <div className="mt-10">
            <h3 className="text-storePage-results-title-text mt-10 inline pr-3">
              {t('results.title', {
                location: `"${isCurrentLocationSearch ? t('results.currentLocation') : location}"`,
              })}
            </h3>
            <button className="text-searchClear-button inline text-sm underline" onClick={resetStoreOrder}>
              {t('results.clearSearch')}
            </button>
          </div>
        )}
        <div className="mb-10 mt-10">
          {filteredStores.map(store => (
            <div className="mb-6" key={store.id}>
              <StoreCard
                ctaButtonType={storeCardCtaButtonType}
                ctaVariant={storeCardCtaVariant}
                hideOpeningHours={store.storetype === 'Geen' && store.isshop}
                store={store}
                translations={t('storeFinder.card', { returnObjects: true })}
                variant="primary"
              />
            </div>
          ))}
        </div>
      </Layout>
    </SEO>
  );
};

export default Index;
